import React, {useContext, useState, useEffect, useCallback, useRef} from "react";
import {useParams} from "react-router";
import styled from "styled-components";

// Utils
import {exists} from "../utils/helpers.js";

// Hooks
import useApi from "../hooks/useApi.js";
import useMountedState from "../hooks/useMountedState.js";

// Contexts
import {FacilityNavContext} from "../contexts/facilitynav.js";
import {AuthContext} from "../contexts/auth.js";
import {NotificationContext} from "../contexts/notify.js";
import {useSocket} from "../contexts/socket.js";

// Components
import Room, {getFacilityRooms, LIST_LOCKS, REQUEST_LOCKS, ROOM_JOINED} from "./general/Room.js";
import FacilityPageHeader from "./general/FacilityPageHeader.js";
import ChecksheetsDue from "./tasks/ChecksheetsDue.js";
import EventsDue from "./tasks/EventsDue.js";

// Style
import {flex} from "../style/components/mixins.js";
import {shake} from "../style/components/animations.js";
import {pad, radius, shadow} from "../style/components/variables.js";
import {Button, Heading, Loader, Page, Text} from "../style/components/general.js";

const Tasks = () => {
  const isMounted = useMountedState();

  const socket = useSocket();

  const {facility, setFacility} = useContext(FacilityNavContext);
  const {roleCanAccessResource, currentUser} = useContext(AuthContext);
  const {getNotifications} = useContext(NotificationContext);

  const {slug} = useParams();

  const [active, setActive] = useState(null);
  const [locks, setLocks] = useState(undefined);
  const [canRequestLocks, setCanRequestLocks] = useState(false);
  const [checksheetHasUpdates, setChecksheetHasUpdates] = useState(false);
  const [eventHasUpdates, setEventHasUpdates] = useState(false);

  const refreshingNotificationsChecksheet = useRef(false);

  const timers = useRef({});

  const {api, loading} = useApi("facilities");

  useEffect(
    () => () => {
      Object.values(timers.current).map(timer => clearTimeout(timer));
    },
    []
  );

  const getFacility = useCallback(
    () =>
      api.callGet(slug).then(({status, data}) => {
        if (status === 200 && data) setFacility(data);
      }),
    [api, setFacility, slug]
  );

  const roomJoined = useCallback(({lockList}) => {
    setLocks(lockList);
  }, []);

  const handleUpdates = useCallback(() => {
    refreshingNotificationsChecksheet.current = true;
    getNotifications(slug);
    setChecksheetHasUpdates(false);
    setEventHasUpdates(false);
  }, [getNotifications, slug]);

  // Initial Load
  useEffect(() => {
    if (facility === null) {
      if (canRequestLocks) setCanRequestLocks(false);
      getFacility();
    }
  }, [facility, getFacility, canRequestLocks]);

  const listLocks = useCallback(
    ({lockList, userId, fromPercentComplete}) => {
      // if type is null, this event should be accepted by both Events and Checksheets
      // if userId is null, then it was another event action that triggered listLocks
      // if userId is defined, it is either from request_locks or percent_complete and should be ignored if:
      //        userId is current user, and request was not from percent complete
      //        userId is not the current user, and request was from percent complete
      if (
        !userId ||
        (userId === currentUser.publicId && !fromPercentComplete) ||
        (userId !== currentUser.publicId && fromPercentComplete)
      ) {
        setLocks(lockList);
      }
    },
    [currentUser]
  );

  useEffect(() => {
    if (locks) {
      Object.keys(locks).map(key => {
        const lockInfo = locks[key];
        const {ttl} = lockInfo;
        if (exists(ttl) && ttl > 0 && ttl <= 3 * 60 * 60) {
          if (timers.current[key]) clearTimeout(timers.current[key]);
          const timer = setTimeout(() => {
            socket.emit(REQUEST_LOCKS, {
              rooms: getFacilityRooms(slug, "checksheet"),
              user: currentUser.publicId,
              type: "checksheet"
            });
            delete timers.current[key];
          }, ttl * 1000);
          timers.current[key] = timer;
        }
      });
    }
  }, [locks, socket, currentUser, slug]);

  // Socket Management
  useEffect(() => {
    if (isMounted() && slug) {
      socket.on(LIST_LOCKS, listLocks);
    }

    return () => {
      // unbind all event handlers used in this component
      socket.off(LIST_LOCKS, listLocks);
    };
  }, [isMounted, socket, slug, listLocks, canRequestLocks]);

  useEffect(() => {
    socket.on(ROOM_JOINED, roomJoined);

    return () => {
      // unbind all event handlers used in this component
      socket.off(ROOM_JOINED, roomJoined);
    };
  }, [isMounted, roomJoined, socket]);

  return (
    <>
      <Page hasMenu>
        <Room name="tasks" active={active} setActive={setActive} withLocks />

        {facility?.name && (
          <FacilityPageHeader
            facility={facility}
            reloadData={() => {
              setFacility(null);
              setCanRequestLocks(false);
            }}
            path="/tasks"
          />
        )}

        {!loading && facility?.id ? (
          <Wrapper>
            {roleCanAccessResource("facility_checksheet_record", "create") ||
            roleCanAccessResource("event_record", "create") ? (
              <>
                <EventsDue
                  room={`${slug}-tasks`}
                  locks={locks}
                  facilityId={facility.id}
                  canRequestLocks={canRequestLocks}
                  setHasUpdates={setEventHasUpdates}
                />
                <ChecksheetsDue
                  room={`${slug}-tasks`}
                  facilityId={facility.id}
                  canRequestLocks={canRequestLocks}
                  setHasUpdates={setChecksheetHasUpdates}
                  refreshingNotifications={refreshingNotificationsChecksheet}
                  locks={locks}
                  handleUpdates={handleUpdates}
                />
              </>
            ) : (
              <div>
                <Heading>Not Authorized</Heading>
                <Text>Your role is not authorized to complete tasks.</Text>
              </div>
            )}
          </Wrapper>
        ) : (
          <Loader />
        )}
      </Page>
      {(checksheetHasUpdates || eventHasUpdates) && (
        <ModificationBanner>
          <ModificationText inverted quiet>
            Tasks have changed since your last refresh.&nbsp;
            <Button inText onClick={handleUpdates}>
              <ButtonText inverted quiet>
                Load New Changes!
              </ButtonText>
            </Button>
          </ModificationText>
        </ModificationBanner>
      )}
    </>
  );
};

// Style Overrides
const Wrapper = styled.div`
  ${flex("column", "nowrap", "start")};
  gap: ${pad * 3}px;
  height: 100%;
`;

const ModificationBanner = styled.div`
  ${flex("column", "nowrap", "start", "center")}
  width: 100%;
  position: sticky;
  bottom: 15px;
`;

const ModificationText = styled(Text)`
  background-color: ${({theme}) => theme.secondary};
  opacity: 0.92;
  padding: ${pad}px;
  border-radius: ${radius};
  box-shadow: ${shadow};
`;

const ButtonText = styled(Text)`
  text-decoration: underline;
  animation: ${shake()} 5s linear 2s infinite;

  &:hover {
    animation-play-state: paused;
  }
`;

export default Tasks;
